import { createContext, useContext, type PropsWithChildren } from 'react';
import { useLogger } from './useLogger';
import { type Logger } from '../models';

type PageLoggerPropsWithPageName = { pageName: string; logger?: never };
type PageLoggerPropsWithLogger = { pageName?: never; logger: Logger };

const PageLoggerContext = createContext<Logger | undefined>(undefined);

const usePageLoggerContext = () => useContext(PageLoggerContext);

/**
 * A Context that holds a logger mapped to a specific page.
 *
 * You can either create a logger by directly entering a page name, or insert an existing logger from outside to pass it to child components.
 *
 * Within child components, use the usePageLogger hook or components like LoggingEvent, LoggingClick, etc. to send events.
 *
 * @example
 * ```tsx
 * // A logger with the context of that page is created.
 * <PageLogger pageName="ReviewsShortcode" />
 *
 * // Passes the logger being used externally directly to the children.
 * // Can be used for child components created using Portal outside the PageLogger Context.
 * const logger = usePageLogger();
 * <PageLogger logger={logger} />
 * ```
 */
export const PageLogger = ({
  pageName,
  logger,
  children,
}: PropsWithChildren<PageLoggerPropsWithPageName | PageLoggerPropsWithLogger>) => {
  const pageLogger = useLogger(pageName ?? '');

  return <PageLoggerContext.Provider value={logger ?? pageLogger}>{children}</PageLoggerContext.Provider>;
};

/**
 * Uses the logger declared in the PageLogger Context located in the parent to send events.
 *
 * If there is no PageLogger Context in the parent, it does not send any events and ignores them.
 *
 * @example
 * ```tsx
 * const logger = usePageLogger();
 *
 * // Log a click event
 * logger?.click('write_review_button', { productId: '123' });
 *
 * // Log a custom event
 * logger?.event('review_form', 'submitted', { rating: 5 });
 * ```
 */
export const usePageLogger = () => {
  return usePageLoggerContext();
};
